import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowUpDown, ArrowUpAZ, ArrowDownZA, ArrowUp10, ArrowDown10,
  MoreVertical, Pencil, Copy, Plus, Search, Sparkles, PencilLine,
} from "lucide-react";

import host from "../../../global.jsx";
import Loader from "../../Loader.jsx";
import ModeSelectionScreen from "./ModeSelectionScreen";

// Matches CommonTable/MCQSetsLibrary so every set-library table reads as one system.
const thCls =
  "text-left px-4 py-4 text-sm font-medium dark:text-[#CCCCCC] text-textSecondary";
const tdCls = "p-4 text-sm dark:text-[#CCCCCC] text-textSecondary";

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const SortIcon = ({ colKey, sortConfig, numeric }) => {
  const cls = "w-4 h-4 ml-2 cursor-pointer dark:hover:text-white hover:text-gray-300";
  if (sortConfig.key !== colKey) return <ArrowUpDown className={`${cls} opacity-40`} />;
  if (sortConfig.direction === "asc")
    return numeric ? <ArrowUp10 className={cls} /> : <ArrowUpAZ className={cls} />;
  return numeric ? <ArrowDown10 className={cls} /> : <ArrowDownZA className={cls} />;
};

// Portal-rendered and fixed off the trigger's rect, not absolutely positioned —
// the table scrolls inside an overflow-x-auto container, which clips absolute
// content on the last rows.
const ROW_MENU_W = 192;
const ROW_MENU_H = 130;

/** Row overflow menu. Closes on any outside click so two menus can't sit open.
 * One addition over MCQ's RowMenu: Duplicate, for recruiters who want a genuinely
 * divergent second version of a set rather than editing this one in place. No
 * Delete — sets aren't deletable from the UI. */
const RowMenu = ({ onEdit, onDuplicate }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", close);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  const toggle = (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const r = btnRef.current.getBoundingClientRect();
    const openDown = window.innerHeight - r.bottom > ROW_MENU_H + 8;
    setPos({
      top: openDown ? r.bottom + 4 : r.top - ROW_MENU_H - 4,
      left: Math.max(8, r.right - ROW_MENU_W),
    });
    setOpen(true);
  };

  const item =
    "w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors";

  return (
    <div className="flex justify-end">
      <button
        ref={btnRef}
        onClick={toggle}
        title="More actions"
        className="p-1.5 rounded-lg dark:text-gray-400 text-textSecondary hover:bg-white/10 transition-colors"
      >
        <MoreVertical size={16} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            onClick={(e) => e.stopPropagation()}
            style={{ top: pos.top, left: pos.left, width: ROW_MENU_W }}
            className="fixed z-[60] rounded-xl border border-gray-400 dark:border-white/10 bg-bgPrimary dark:glass-effect shadow-2xl py-1 overflow-hidden"
          >
            <button
              onClick={() => { setOpen(false); onEdit(); }}
              className={`${item} dark:text-gray-200 text-textPrimary hover:bg-white/10`}
            >
              <Pencil size={14} /> Edit
            </button>
            <button
              onClick={() => { setOpen(false); onDuplicate(); }}
              className={`${item} dark:text-gray-200 text-textPrimary hover:bg-white/10`}
            >
              <Copy size={14} /> Duplicate
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

/** Split button: one primary action, with the build-method choice behind the caret.
 * Copy of MCQSetsLibrary.jsx's CreateSetButton (lines 137-167) — same content-free
 * component pattern, not shared across features (matches how RowMenu/SortIcon above
 * are already duplicated per-library in this codebase). */
const CreateSetButton = ({ onManual, onAI }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((v) => !v)} className="button flex items-center gap-2">
        <Plus size={15} /> Create Set
      </button>

      {open && (
        <div className="absolute right-0 top-12 z-20 w-60 rounded-xl border border-gray-400 dark:border-white/10 bg-bgPrimary dark:glass-effect shadow-2xl py-1 overflow-hidden">
          <button
            onClick={() => { setOpen(false); onManual(); }}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-base text-left dark:text-gray-200 text-textPrimary hover:bg-white/10 transition-colors"
          >
            <PencilLine size={14} className="text-blue-400" /> Start from defaults
          </button>
          <button
            onClick={() => { setOpen(false); onAI(); }}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-base text-left dark:text-gray-200 text-textPrimary hover:bg-white/10 transition-colors"
          >
            <Sparkles size={14} className="text-blue-400" /> Generate with AI
          </button>
        </div>
      )}
    </div>
  );
};

const BehavioralSetsLibrary = () => {
  const navigate = useNavigate();
  const accessToken = Cookies.get("accessToken");
  const authHeaders = { Authorization: `Bearer ${accessToken}` };

  const [sets, setSets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${host}/behavioral-sets`, { headers: authHeaders });
      setSets(response.data.sets || []);
    } catch {
      toast.error("Error fetching data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const goNew = () => navigate("/org-admin/behavioralsets/new");
  const goAI = () => navigate("/org-admin/behavioralsets/ai");

  const toggleSort = (key) =>
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: key === "name" ? "asc" : "desc" }
    );

  const visibleSets = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? sets.filter((s) => (s.name || "").toLowerCase().includes(needle))
      : [...sets];

    const { key, direction } = sortConfig;
    const factor = direction === "asc" ? 1 : -1;

    return filtered.sort((a, b) => {
      if (key === "name")
        return factor * (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
      if (key === "question_count")
        return factor * ((a.question_count || 0) - (b.question_count || 0));
      const at = a.created_at ? new Date(a.created_at).getTime() : null;
      const bt = b.created_at ? new Date(b.created_at).getTime() : null;
      if (at === null && bt === null) return 0;
      if (at === null) return 1;
      if (bt === null) return -1;
      return factor * (at - bt);
    });
  }, [sets, query, sortConfig]);

  const handleDuplicate = async (set) => {
    try {
      await axios.post(`${host}/behavioral-sets/${set.id}/duplicate`, {}, { headers: authHeaders });
      toast.success("Set duplicated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error duplicating set");
    }
  };

  if (isLoading) return <Loader />;

  return (
      <div className="min-h-screen">
        <div className="bg-bgPrimary flex flex-col min-h-screen border border-gray-700/50 rounded-2xl pt-4 pb-16">
          <div className="flex items-center justify-between gap-4 px-6 pt-2 mb-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/org-admin/testlibrary")}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
              </button>
              <span className="dark:text-gray-400 text-textSecondary">Behavioural Sets</span>
            </div>
            {sets.length > 0 && <CreateSetButton onManual={goNew} onAI={goAI} />}
          </div>

          <div className="w-full flex-1 mt-4 px-6">
            {/* A first-run org has nothing to list, so the page becomes the choice of
                how to start rather than an empty table (mirrors MCQSetsLibrary.jsx). */}
            {sets.length === 0 ? (
              <ModeSelectionScreen
                onSelectMode={(mode) => (mode === "ai" ? goAI() : goNew())}
              />
            ) : (
              <>
                <div className="relative mb-4 max-w-md">
                  <Search
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-gray-500 text-textSecondary"
                  />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search sets…"
                    className="dark:glass-effect bg-bgSecondary dark:!text-white !text-textPrimary border border-gray-400 dark:border-white/10 rounded-xl w-full pl-9 pr-3 py-2.5 text-sm outline-none transition-colors"
                  />
                </div>

                <div className="rounded-lg border-gray-700/50 overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-bgSecondary">
                      <tr className="border-b border-[#5A4479D4]">
                        <th className={`${thCls} cursor-pointer`} onClick={() => toggleSort("name")}>
                          <div className="flex items-center">
                            Name <SortIcon colKey="name" sortConfig={sortConfig} />
                          </div>
                        </th>
                        <th
                          className={`${thCls} cursor-pointer w-40`}
                          onClick={() => toggleSort("question_count")}
                        >
                          <div className="flex items-center">
                            Questions{" "}
                            <SortIcon colKey="question_count" sortConfig={sortConfig} numeric />
                          </div>
                        </th>
                        <th
                          className={`${thCls} cursor-pointer w-44`}
                          onClick={() => toggleSort("created_at")}
                        >
                          <div className="flex items-center">
                            Created on{" "}
                            <SortIcon colKey="created_at" sortConfig={sortConfig} numeric />
                          </div>
                        </th>
                        <th className={`${thCls} w-20 pr-6`} />
                      </tr>
                    </thead>

                    <tbody>
                      {visibleSets.length === 0 && (
                        <tr>
                          <td colSpan={4} className="h-72 text-center">
                            <div className="flex h-full flex-col items-center justify-center">
                              <h2 className="text-lg font-medium text-gray-400 mb-1">
                                No sets found
                              </h2>
                              <p className="text-sm text-gray-400">
                                Try a different search term.
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}

                      {visibleSets.map((s) => (
                        <tr
                          key={s.id}
                          onClick={() => navigate(`/org-admin/behavioralsets/${s.id}`)}
                          className="border-b dark:border-gray-700/50 border-gray-300/50 dark:hover:bg-gray-800 hover:bg-gray-100 transition-colors cursor-pointer group"
                        >
                          <td className={`${tdCls} font-medium`}>
                            <span className="dark:text-white text-textPrimary inline-flex items-center gap-1.5">
                              {s.name}
                            </span>
                          </td>
                          <td className={tdCls}>
                            {s.question_count} question{s.question_count === 1 ? "" : "s"}
                          </td>
                          <td className={tdCls}>{formatDate(s.created_at)}</td>
                          <td className={`${tdCls} pr-6`}>
                            <RowMenu
                              onEdit={() => navigate(`/org-admin/behavioralsets/${s.id}`)}
                              onDuplicate={() => handleDuplicate(s)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
  );
};

export default BehavioralSetsLibrary;
